import { lazy, ReactNode, Suspense } from 'react';
import { LuDownload, LuX } from 'react-icons/lu';

import { OnboardingFileView } from '@ndvr/customer-api';

import { PageSkeleton } from '@/components/pdf-viewer/PageSkeleton';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useObjectUrl } from '@/hooks/useObjectUrl';

import { onboardingFileUrl, useOnboardingFileContent } from './onboarding-tab-queries';

// pdf.js loads with the first PDF opened rather than with the app; vite.config.ts keeps it out of the vendor chunk
const PdfViewer = lazy(() =>
    import('@/components/pdf-viewer/PdfViewer').then((module) => ({ default: module.PdfViewer })),
);

const Backdrop = ({ children }: { children: ReactNode }) => (
    <div className="bg-cocoa-50 min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
);

const LoadingBackdrop = () => (
    <Backdrop>
        <PageSkeleton />
    </Backdrop>
);

const PreviewError = () => (
    <p className="text-cocoa-600 py-10 text-center text-sm">
        This file cannot be shown here. Download it to open it on your computer.
    </p>
);

const BlobImage = ({ blob, alt }: { blob: Blob; alt: string }) => {
    const url = useObjectUrl(blob);
    return url && <img src={url} alt={alt} className="mx-auto max-w-full bg-white shadow-sm" />;
};

/** Sized like InvoicePreviewDialog: a letter page fits its width at about 100%, where full screen would enlarge it 2×. */
export const FilePreviewDialog = ({
    householdGUID,
    file,
    onClose,
}: {
    householdGUID: string;
    file: OnboardingFileView;
    onClose: () => void;
}) => {
    const contentQuery = useOnboardingFileContent(householdGUID, file);

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                showCloseButton={false}
                aria-describedby={undefined}
                className="flex h-[90vh] max-w-4xl flex-col gap-0 overflow-hidden bg-white p-0"
            >
                <div className="border-border flex items-center gap-3 border-b px-4 py-3">
                    <DialogTitle className="min-w-0 flex-1 truncate text-base">{file.fileName}</DialogTitle>
                    {/* The row's link, so it works even when the preview fails */}
                    <Button asChild variant="outline" size="sm">
                        <a href={onboardingFileUrl(householdGUID, file)} download>
                            <LuDownload className="h-4 w-4" />
                            Download
                        </a>
                    </Button>
                    <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none">
                        <LuX className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                    </DialogClose>
                </div>
                {contentQuery.isPending && <LoadingBackdrop />}
                {contentQuery.isError && (
                    <Backdrop>
                        <PreviewError />
                    </Backdrop>
                )}
                {contentQuery.data &&
                    (file.contentType === 'application/pdf' ? (
                        <Suspense fallback={<LoadingBackdrop />}>
                            <PdfViewer file={contentQuery.data} error={<PreviewError />} />
                        </Suspense>
                    ) : (
                        <Backdrop>
                            <BlobImage blob={contentQuery.data} alt={file.fileName} />
                        </Backdrop>
                    ))}
            </DialogContent>
        </Dialog>
    );
};
